import type { Bounds, GameCoord } from "./coords.ts";
import { boundsHeight, boundsWidth } from "./coords.ts";

/**
 * Hierarchical callout grid.
 *
 * The top level is the sector grid the community already uses (A1 .. H8 by
 * default). Below that we subdivide numpad-style, the way Squad and Post
 * Scriptum do it, so a callout can be as coarse or as precise as the situation
 * allows:
 *
 *   "D5"       -> a whole sector, roughly a Control Zone
 *   "D5-7"     -> one ninth of it
 *   "D5-7-3"   -> one eighty-first of it
 *
 * Numpad layout, so 7 is north-west and 3 is south-east:
 *
 *   7 8 9
 *   4 5 6
 *   1 2 3
 */

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export interface GridConfig {
  /** Rectangle the grid is laid over — normally the map's playable bounds. */
  readonly bounds: Bounds;
  /** Number of sector columns (and rows) across the grid. */
  readonly divisions: number;
}

export interface GridRef {
  /** Sector label, e.g. `"D5"`. */
  readonly sector: string;
  /** Successive numpad subdivisions, outermost first. */
  readonly keypads: readonly number[];
  /** Full callout label, e.g. `"D5-7-3"`. */
  readonly label: string;
}

/**
 * The game's grid: 16 x 16 cells of one kilometre, lettered A-P.
 *
 * This is not a guess. The world is 163.84 units per side, and 163.84 / 16 is
 * 10.24 units — 1.024 km. A 16 x 16 grid over the full render is the only
 * division that produces the "1 km cells, A-P by 1-16" the community
 * documents, and it falls out of the world size exactly.
 *
 * Note it is laid over the FULL render, not the playable rectangle. A grid
 * that started at the playable edge would shift between rotations, and a
 * callout has to mean the same thing on every map.
 */
export const DEFAULT_DIVISIONS = 16;

/**
 * Sector edge length in game units. With the default 8 divisions over a
 * ~110-unit playable area this lands near 13.8 units (1.38 km) per sector.
 */
export function sectorSizeUnits(config: GridConfig): number {
  return boundsWidth(config.bounds) / config.divisions;
}

/**
 * Convert a position into a callout reference.
 *
 * `depth` is how many numpad subdivisions to include: 0 gives a bare sector,
 * 1 gives `D5-7`, 2 gives `D5-7-3`. Positions outside the grid are clamped to
 * the nearest sector so a stray click still produces a usable callout.
 */
export function toGridRef(point: GameCoord, config: GridConfig, depth = 1): GridRef {
  const { bounds, divisions } = config;
  const width = boundsWidth(bounds);
  const height = boundsHeight(bounds);

  const colWidth = width / divisions;
  const rowHeight = height / divisions;

  // Column index grows with x (west to east).
  const rawCol = Math.floor((point.x - bounds.minX) / colWidth);
  // Row index grows downward from the north edge, so row 1 is the top.
  const rawRow = Math.floor((bounds.maxY - point.y) / rowHeight);

  const col = clampIndex(rawCol, divisions);
  const row = clampIndex(rawRow, divisions);

  const letter = LETTERS[col] ?? "A";
  const sector = `${letter}${row + 1}`;

  // Origin of this sector: west edge, north edge.
  let cellMinX = bounds.minX + col * colWidth;
  let cellMaxY = bounds.maxY - row * rowHeight;
  let cellWidth = colWidth;
  let cellHeight = rowHeight;

  const keypads: number[] = [];
  for (let level = 0; level < depth; level++) {
    cellWidth /= 3;
    cellHeight /= 3;

    const subCol = clampIndex(Math.floor((point.x - cellMinX) / cellWidth), 3);
    const subRow = clampIndex(Math.floor((cellMaxY - point.y) / cellHeight), 3);

    keypads.push(numpadFor(subCol, subRow));

    cellMinX += subCol * cellWidth;
    cellMaxY -= subRow * cellHeight;
  }

  const label = keypads.length > 0 ? `${sector}-${keypads.join("-")}` : sector;
  return { sector, keypads, label };
}

/** Shorthand for the full callout label of a position. */
export function gridLabel(point: GameCoord, config: GridConfig, depth = 1): string {
  return toGridRef(point, config, depth).label;
}

/**
 * Resolve a callout reference back to the centre of the square it names.
 * Returns `undefined` if the reference cannot be parsed or falls off the grid.
 */
export function fromGridRef(label: string, config: GridConfig): GameCoord | undefined {
  const cleaned = label.trim().toUpperCase();
  const match = /^([A-Z])\s*(\d+)((?:\s*-\s*[1-9])*)$/.exec(cleaned);
  if (!match) return undefined;

  const letter = match[1];
  const rowText = match[2];
  if (letter === undefined || rowText === undefined) return undefined;

  const col = LETTERS.indexOf(letter);
  const row = Number(rowText) - 1;
  if (col < 0 || col >= config.divisions) return undefined;
  if (!Number.isInteger(row) || row < 0 || row >= config.divisions) return undefined;

  const { bounds, divisions } = config;
  let cellWidth = boundsWidth(bounds) / divisions;
  let cellHeight = boundsHeight(bounds) / divisions;
  let cellMinX = bounds.minX + col * cellWidth;
  let cellMaxY = bounds.maxY - row * cellHeight;

  const tail = match[3] ?? "";
  const keypads = tail.match(/[1-9]/g) ?? [];
  for (const raw of keypads) {
    const pad = Number(raw);
    cellWidth /= 3;
    cellHeight /= 3;
    const { col: subCol, row: subRow } = numpadToCell(pad);
    cellMinX += subCol * cellWidth;
    cellMaxY -= subRow * cellHeight;
  }

  return { x: cellMinX + cellWidth / 2, y: cellMaxY - cellHeight / 2 };
}

/** Column/row indices of every sector line, for drawing the grid overlay. */
export function gridLines(config: GridConfig): { xs: number[]; ys: number[] } {
  const { bounds, divisions } = config;
  const colWidth = boundsWidth(bounds) / divisions;
  const rowHeight = boundsHeight(bounds) / divisions;

  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i <= divisions; i++) {
    xs.push(bounds.minX + i * colWidth);
    ys.push(bounds.minY + i * rowHeight);
  }
  return { xs, ys };
}

/** Labels and anchor points for each sector, for rendering the overlay. */
export function sectorLabels(config: GridConfig): Array<{ label: string; at: GameCoord }> {
  const { bounds, divisions } = config;
  const colWidth = boundsWidth(bounds) / divisions;
  const rowHeight = boundsHeight(bounds) / divisions;

  const result: Array<{ label: string; at: GameCoord }> = [];
  for (let row = 0; row < divisions; row++) {
    for (let col = 0; col < divisions; col++) {
      const letter = LETTERS[col] ?? "A";
      result.push({
        label: `${letter}${row + 1}`,
        at: {
          x: bounds.minX + (col + 0.5) * colWidth,
          y: bounds.maxY - (row + 0.5) * rowHeight,
        },
      });
    }
  }
  return result;
}

function clampIndex(value: number, size: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(size - 1, Math.max(0, value));
}

/** Map a (col, row) pair inside a 3x3 cell to its numpad digit. */
function numpadFor(col: number, row: number): number {
  // row 0 is the northern band -> digits 7 8 9
  return (2 - row) * 3 + col + 1;
}

/** Inverse of {@link numpadFor}. */
function numpadToCell(pad: number): { col: number; row: number } {
  const index = pad - 1;
  const band = Math.floor(index / 3);
  return { col: index % 3, row: 2 - band };
}
